import WebPhone from 'ringcentral-web-phone';
import { RC_FROM_NUMBER, RC_SERVER_URL } from './config';
// eslint-disable-next-line import/no-cycle
import { setActiveSession } from './session';

let webPhoneInstance = null;
let remoteAudioElement = null;
let localAudioElement = null;

const requestSipProvision = async (accessToken) => {
  const res = await fetch(
    `${RC_SERVER_URL}/restapi/v1.0/client-info/sip-provision`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sipInfo: [{ transport: 'WSS' }] }),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`SIP provision failed: ${text}`);
  }
  return res.json();
};

// RINGBACK (2026-08-28). The SDK only creates `session.audioElement` inside its
// rtcPeerConnection.ontrack handler, and for an outbound call the FIRST media to
// arrive is the ringback itself (early media on the 183). But `webPhone.call()`
// deliberately does not resolve until the callee ANSWERS ("wait for the call to
// be answered", ringcentral-web-phone/call-session/outbound.js), so wiring our
// <audio> element after that await left the entire ringing phase silent: our
// element had no stream yet, and the element the SDK makes for itself is created
// detached and never added to the page, so whether anyone heard ringback came
// down to browser autoplay policy. Some people heard it, some heard nothing.
//
// Fix: attach as soon as the stream exists rather than when the call connects.
// We poll instead of listening for one event because ontrack can fire again
// mid-call (a re-INVITE on hold/unhold builds a new element), which would
// otherwise orphan ours and kill audio for the rest of the call. Attaching is
// idempotent and only touches the element when the stream actually changed.
const AUDIO_ATTACH_POLL_MS = 100;

// Two sessions at once (a warm transfer, 2026-10-29: the client on hold and
// the consult with the specialist) share the one <audio> element, so only
// the FOREGROUND session may play through it. The stream itself is read
// from the SDK's own detached element and never aliased onto ours: the SDK
// nulls its element's srcObject on dispose, and an aliased consult ending
// would have silenced the client for the rest of the call.
let foregroundSession = null;
const streamOf = new WeakMap();
const sessionStream = (session) => {
  const fresh = session?.audioElement?.srcObject;
  if (fresh) streamOf.set(session, fresh);
  return fresh || streamOf.get(session) || null;
};

export const setForegroundSession = (session) => {
  foregroundSession = session || null;
  if (session) attachSessionAudio(session);
};
export const getForegroundSession = () => foregroundSession;

const attachSessionAudio = (session) => {
  const stream = sessionStream(session);
  if (!remoteAudioElement || !stream) return false;
  if (foregroundSession && session !== foregroundSession) return false;
  if (remoteAudioElement.srcObject === stream) return true;
  remoteAudioElement.srcObject = stream;
  const p = remoteAudioElement.play();
  if (p && typeof p.catch === 'function') p.catch(() => {});
  return true;
};

const watchSessionAudio = (session) => {
  if (!session) return;
  attachSessionAudio(session);
  const timer = setInterval(() => {
    if (session.state === 'disposed') {
      clearInterval(timer);
      return;
    }
    attachSessionAudio(session);
  }, AUDIO_ATTACH_POLL_MS);
  try {
    session.on('disposed', () => {
      clearInterval(timer);
      if (foregroundSession === session) foregroundSession = null;
    });
  } catch (_) {
    // An SDK without the event still stops via the state check above.
  }
};

// The next session the SDK dials, handed over BEFORE it rings (the SDK's
// own call() only resolves on the SIP answer). The warm transfer uses it to
// hold the consult while it rings.
let nextOutboundWaiters = [];
export const captureNextOutboundSession = () =>
  new Promise((resolve) => {
    nextOutboundWaiters.push(resolve);
  });
const handleOutboundCall = (session) => {
  watchSessionAudio(session);
  const waiters = nextOutboundWaiters;
  nextOutboundWaiters = [];
  waiters.forEach((w) => w(session));
};

// The cached registration is only worth reusing while its SIP WebSocket is
// actually OPEN. A long-lived tab's registration dies quietly (overnight
// sleep, network change, RC-side expiry) and a call through the corpse
// "succeeds" locally while the INVITE goes nowhere — the ghost-call report
// (Kit, overnight tab). readyState is the SDK's own public socket
// (sipClient.wsc); anything but OPEN means re-provision.
const isWebPhoneHealthy = (wp) => {
  try {
    if (wp.disposed) return false;
    const ws = wp.sipClient?.wsc;
    // SDK shape changed and hid the socket: keep the old always-reuse
    // behavior rather than re-provisioning every call.
    if (!ws) return true;
    return ws.readyState === WebSocket.OPEN;
  } catch (_) {
    return true;
  }
};

export const ensureWebPhone = async (accessToken) => {
  if (webPhoneInstance) {
    if (isWebPhoneHealthy(webPhoneInstance)) return webPhoneInstance;
    // Dead registration: silently discard and fall through to a fresh
    // provision — self-heal, nothing surfaced to the user.
    resetWebPhone();
  }
  const provision = await requestSipProvision(accessToken);
  const wp = new WebPhone({ sipInfo: provision.sipInfo[0] });
  // Fires with the session BEFORE the SDK blocks waiting for an answer, which
  // is the only hook early enough to catch ringback. Covers switchInCall too,
  // since that also dials through webPhone.call().
  wp.on('outboundCall', handleOutboundCall);
  await wp.start();
  webPhoneInstance = wp;
  return wp;
};

// A long-lived tab's SIP registration can die quietly (laptop sleep, network
// change) — calls then dial into the void. Dropping the singleton forces the
// next ensureWebPhone to provision a FRESH registration; callers use this to
// self-heal with one retry after a failed dial.
export const resetWebPhone = () => {
  const wp = webPhoneInstance;
  webPhoneInstance = null;
  if (!wp) return;
  try {
    Promise.resolve(wp.dispose?.()).catch(() => {});
  } catch (_) {}
};

// `fromNumber` (optional) overrides the app-wide caller ID for this call —
// the Onboarding Support board presents the signed-in person's own line
// this way (personal-numbers model 2026-08-10).
export const placeOutboundCall = async (webPhone, e164Number, fromNumber) => {
  const session = await webPhone.call(
    e164Number,
    fromNumber || RC_FROM_NUMBER || undefined,
  );
  // Normally already wired by the outboundCall watcher; idempotent re-check so a
  // session that somehow missed that path is still connected once it is up.
  setForegroundSession(session);
  if (localAudioElement && session.mediaStream) {
    localAudioElement.srcObject = session.mediaStream;
    const p2 = localAudioElement.play();
    if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
  }
  setActiveSession(session);
  const cleanup = () => {
    if (remoteAudioElement) {
      try {
        remoteAudioElement.pause();
      } catch (_) {}
      try {
        remoteAudioElement.srcObject = null;
      } catch (_) {}
    }
    if (localAudioElement) {
      try {
        localAudioElement.pause();
      } catch (_) {}
      try {
        localAudioElement.srcObject = null;
      } catch (_) {}
    }
  };
  session.on('disposed', cleanup);
  return session;
};

export const setAudioElements = (remoteEl, localEl) => {
  remoteAudioElement = remoteEl || null;
  localAudioElement = localEl || null;
};

// "Switch call to this device" (2026-08-12): pull an ONGOING call (whose
// browser handle was lost, e.g. to a refresh) into a fresh WebRTC session by
// sending an INVITE with a Replaces header — the same mechanism the official
// RC apps use for their "take control" button. Pure SIP; needs no Call
// Control REST permission. The callee number is irrelevant ("whatever" per
// the SDK docs) because nothing new is dialed; a call picked up from the
// RingCentral app (2026-11-02) passes the real other-party number instead,
// since the SDK addresses its in-dialog REFER and re-INVITE at whatever was
// dialed. `replaces` is the finished header value when the caller built it
// (the pick-up's tag-order retries); otherwise the tags are used as given.
// The SDK resolves the session even when the INVITE is refused (state
// 'failed', already disposed), so a refusal is thrown here, never adopted.
export const switchInCall = async (
  webPhone,
  { telephonySessionId, toTag, fromTag, callee = 'whatever', replaces = null },
) => {
  const captured = captureNextOutboundSession();
  const pending = webPhone.call(callee, undefined, {
    headers: {
      Replaces:
        replaces || `${telephonySessionId};to-tag=${toTag};from-tag=${fromTag};early-only`,
    },
  });
  let subject = null;
  try {
    // call() can reject before it ever emits the session (a dead
    // registration): never wait on a session that will not come.
    const early = await Promise.race([
      captured,
      pending.then(
        () => null,
        () => null,
      ),
    ]);
    if (early) {
      early.on('failed', (s) => {
        subject = s;
      });
    }
  } catch (_) {}
  const session = await pending;
  if (session?.state !== 'answered') {
    const err = new Error(subject || 'Switch refused');
    err.code = 'switch_refused';
    err.subject = subject;
    try {
      session?.dispose?.();
    } catch (_) {}
    throw err;
  }
  // Normally already wired by the outboundCall watcher; idempotent re-check so a
  // session that somehow missed that path is still connected once it is up.
  setForegroundSession(session);
  if (localAudioElement && session.mediaStream) {
    localAudioElement.srcObject = session.mediaStream;
    const p2 = localAudioElement.play();
    if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
  }
  setActiveSession(session);
  const cleanup = () => {
    if (remoteAudioElement) {
      try {
        remoteAudioElement.pause();
      } catch (_) {}
      try {
        remoteAudioElement.srcObject = null;
      } catch (_) {}
    }
    if (localAudioElement) {
      try {
        localAudioElement.pause();
      } catch (_) {}
      try {
        localAudioElement.srcObject = null;
      } catch (_) {}
    }
  };
  session.on('disposed', cleanup);
  return session;
};
